import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Link2, Loader2, Plus, Search, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

// ===============================
// Generic Link Entity Popover
// ===============================

export interface LinkableEntity {
    id: number;
    label: string;
    sublabel?: string | null;
    badge?: string | null;
}

interface LinkEntityPopoverProps {
    existingIds: number[];
    onSearch: (query: string) => Promise<LinkableEntity[]>;
    onSelect: (id: number) => Promise<unknown> | void;
    placeholder?: string;
    emptyMessage?: string;
    noItemsMessage?: string;
    icon: LucideIcon;
    buttonVariant?: "ghost" | "outline";
    /** Render an icon-only trigger to save horizontal space in narrow layouts. */
    compact?: boolean;
    /** When provided, shows a "Create new" action inside the popover. */
    onCreateNew?: () => void;
    createNewLabel?: string;
    /** Render the picker inside a centered modal dialog instead of an inline expansion. */
    asDialog?: boolean;
    /** Title shown at the top of the dialog when asDialog is set. */
    dialogTitle?: string;
}

export function LinkEntityPopover({
    existingIds,
    onSearch,
    onSelect,
    placeholder = "Search...",
    emptyMessage = "No matches",
    noItemsMessage = "None available",
    icon: Icon,
    buttonVariant = "ghost",
    compact = false,
    onCreateNew,
    createNewLabel = "Create new",
    asDialog = false,
    dialogTitle = "Link or create",
}: LinkEntityPopoverProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<LinkableEntity[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const requestIdRef = useRef(0);

    const doSearch = useCallback(async (query: string) => {
        const requestId = ++requestIdRef.current;
        setIsSearching(true);
        try {
            const items = await onSearch(query);
            if (requestIdRef.current !== requestId) return;
            setResults(items);
            setHasLoaded(true);
        } catch {
            if (requestIdRef.current !== requestId) return;
            setResults([]);
        } finally {
            if (requestIdRef.current === requestId) {
                setIsSearching(false);
            }
        }
    }, [onSearch]);

    // Load initial results when opened; invalidate in-flight searches on close
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 0);
            doSearch("");
        } else {
            // Bump requestId so any in-flight search responses are discarded
            requestIdRef.current++;
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
            setResults([]);
            setHasLoaded(false);
            setIsSearching(false);
        }
    }, [open, doSearch]);

    // Debounced search on typing
    useEffect(() => {
        if (!open) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => { doSearch(search); }, 300);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [search, open, doSearch]);

    // Filter out already-linked items from results
    const available = results.filter((item) => !existingIds.includes(item.id));

    const handleSelect = async (id: number) => {
        try {
            await onSelect(id);
            setSearch("");
            doSearch("");
        } catch {
            // Error handled by caller
        }
    };

    const searchBody = (
        <div className={asDialog ? "space-y-2" : "mt-2 space-y-1.5 w-full col-span-full"}>
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    ref={inputRef}
                    placeholder={placeholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-8 pl-8 text-sm"
                />
                {isSearching && (
                    <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground animate-spin" />
                )}
            </div>
            <div className={`${asDialog ? "max-h-[300px]" : "max-h-[180px]"} overflow-y-auto overscroll-contain rounded-md border`}>
                {!hasLoaded && isSearching ? (
                    <p className="text-sm text-muted-foreground text-center py-3">Loading...</p>
                ) : available.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">
                        {hasLoaded && results.length === 0 && !search ? noItemsMessage : emptyMessage}
                    </p>
                ) : (
                    available.slice(0, 15).map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => handleSelect(item.id)}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2"
                        >
                            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="truncate flex-1">{item.label}</span>
                            {item.badge && (
                                <span className="text-[11px] px-1.5 py-0.5 shrink-0 rounded-full bg-secondary text-secondary-foreground">
                                    {item.badge}
                                </span>
                            )}
                        </button>
                    ))
                )}
            </div>
            {onCreateNew && (
                <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    className="h-8 w-full text-xs justify-start"
                    onClick={() => { setOpen(false); setSearch(""); onCreateNew(); }}
                >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />{createNewLabel}
                </Button>
            )}
        </div>
    );

    if (asDialog) {
        return (
            <>
                <Button
                    variant={buttonVariant}
                    size={compact ? "icon" : "sm"}
                    type="button"
                    title={onCreateNew ? "Link or create" : "Link existing"}
                    aria-label={onCreateNew ? "Link or create" : "Link existing"}
                    className={compact ? "h-7 w-7" : "h-7 text-xs"}
                    onClick={() => setOpen(true)}
                >
                    {compact ? <Plus className="h-3.5 w-3.5" /> : (<><Link2 className="h-3.5 w-3.5" /><span className="ml-1">Link</span></>)}
                </Button>
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(""); }}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>{dialogTitle}</DialogTitle>
                        </DialogHeader>
                        {searchBody}
                    </DialogContent>
                </Dialog>
            </>
        );
    }

    return (
        <>
            {!open ? (
                <Button
                    variant={buttonVariant}
                    size={compact ? "icon" : "sm"}
                    type="button"
                    title={onCreateNew ? "Link or create" : "Link existing"}
                    aria-label={onCreateNew ? "Link or create" : "Link existing"}
                    className={compact ? "h-7 w-7" : "h-7 text-xs"}
                    onClick={() => setOpen(true)}
                >
                    {compact ? <Plus className="h-3.5 w-3.5" /> : (<><Link2 className="h-3.5 w-3.5" /><span className="ml-1">Link</span></>)}
                </Button>
            ) : (
                <Button
                    variant="ghost"
                    size={compact ? "icon" : "sm"}
                    type="button"
                    title="Cancel"
                    aria-label="Cancel"
                    className={compact ? "h-7 w-7" : "h-7 text-xs"}
                    onClick={() => { setOpen(false); setSearch(""); }}
                >
                    <X className="h-3.5 w-3.5" />{!compact && <span className="ml-1">Cancel</span>}
                </Button>
            )}
            {open && searchBody}
        </>
    );
}

// ===============================
// Generic Linked Entity Row
// ===============================

interface LinkedEntityRowProps {
    icon: LucideIcon;
    label: string;
    badge?: string | null;
    onNavigate?: () => void;
    onUnlink?: () => void;
}

export function LinkedEntityRow({ icon: Icon, label, badge, onNavigate, onUnlink }: LinkedEntityRowProps) {
    const content = (
        <>
            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="truncate font-medium flex-1 min-w-0">{label}</span>
            {badge && (
                <span className="text-[10px] px-1.5 py-0 shrink-0 max-w-[45%] truncate rounded-full border">{badge}</span>
            )}
        </>
    );
    return (
        <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm group hover:bg-accent">
            {onNavigate ? (
                <button type="button" onClick={onNavigate} className="flex items-center gap-2 min-w-0 text-left flex-1">
                    {content}
                </button>
            ) : (
                <div className="flex items-center gap-2 min-w-0 flex-1">{content}</div>
            )}
            {onUnlink && (
                <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    aria-label="Remove link"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                    onClick={onUnlink}
                >
                    <X className="h-3.5 w-3.5" />
                </Button>
            )}
        </div>
    );
}
